'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface StoricaEvento {
  id: string;
  tipo: string;
  titolo: string;
  descrizione: string;
  dettagli: Record<string, unknown>;
  device_id: string | null;
  reparto_id: string | null;
  zona_id: string | null;
  allarme_id: string | null;
  utente_id: string | null;
  ip_address: string;
  severita: string;
  created_at: string;
  devices?: { hostname: string } | null;
  reparti?: { nome: string } | null;
  zone_fisiche?: { nome: string } | null;
  user_profiles?: { full_name: string } | null;
}

interface ReportGiornaliero {
  id: string;
  data_report: string;
  totale_allarmi: number;
  allarmi_critici: number;
  allarmi_risolti: number;
  allarmi_aperti: number;
  dispositivi_offline: number;
  dispositivi_online: number;
  nuovi_dispositivi: number;
  conflitti_ip: number;
  scadenze_prossime: number;
  ticket_aperti: number;
  ticket_escalation: number;
  inviato_email: boolean;
  destinatari: string[];
  created_at: string;
}

const EVENTO_TIPO_LABELS: Record<string, string> = {
  allarme_creato: 'Allarme Creato',
  allarme_riconosciuto: 'Allarme Riconosciuto',
  allarme_risolto: 'Allarme Risolto',
  notifica_inviata: 'Notifica Inviata',
  dispositivo_offline: 'Dispositivo Offline',
  dispositivo_online: 'Dispositivo Online',
  scadenza_rilevata: 'Scadenza Rilevata',
  conflitto_ip: 'Conflitto IP',
  nuovo_dispositivo: 'Nuovo Dispositivo',
  firmware_aggiornato: 'Firmware Aggiornato',
  banda_superata: 'Banda Superata',
  temperatura_anomalia: 'Temperatura Anomalia',
  ticket_escalation: 'Ticket Escalation',
  accesso_fisico: 'Accesso Fisico',
  configurazione_modificata: 'Configurazione Modificata',
  altro: 'Altro',
};

const EVENTO_TIPO_ICONS: Record<string, string> = {
  allarme_creato: 'BellAlertIcon',
  allarme_riconosciuto: 'CheckCircleIcon',
  allarme_risolto: 'CheckBadgeIcon',
  notifica_inviata: 'EnvelopeIcon',
  dispositivo_offline: 'WifiIcon',
  dispositivo_online: 'WifiIcon',
  scadenza_rilevata: 'CalendarIcon',
  conflitto_ip: 'ExclamationTriangleIcon',
  nuovo_dispositivo: 'DevicePhoneMobileIcon',
  firmware_aggiornato: 'CpuChipIcon',
  banda_superata: 'ChartBarIcon',
  temperatura_anomalia: 'FireIcon',
  ticket_escalation: 'ArrowUpCircleIcon',
  accesso_fisico: 'KeyIcon',
  configurazione_modificata: 'WrenchScrewdriverIcon',
  altro: 'InformationCircleIcon',
};

const SEVERITA_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  critico: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-500' },
  alto: { bg: 'bg-orange-500/20', text: 'text-orange-400', dot: 'bg-orange-500' },
  medio: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', dot: 'bg-yellow-500' },
  basso: { bg: 'bg-green-500/20', text: 'text-green-400', dot: 'bg-green-500' },
  info: { bg: 'bg-gray-500/20', text: 'text-gray-400', dot: 'bg-gray-400' },
};

export default function StoricoEventiContent() {
  const [activeTab, setActiveTab] = useState<'eventi' | 'report'>('eventi');
  const [eventi, setEventi] = useState<StoricaEvento[]>([]);
  const [report, setReport] = useState<ReportGiornaliero[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTipo, setFilterTipo] = useState('tutti');
  const [filterSeverita, setFilterSeverita] = useState('tutti');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterReparto, setFilterReparto] = useState('tutti');
  const [reparti, setReparti] = useState<{ id: string; nome: string }[]>([]);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('storico_eventi')
        .select('*, devices(hostname), reparti(nome), zone_fisiche(nome), user_profiles(full_name)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filterTipo !== 'tutti') query = query.eq('tipo', filterTipo);
      if (filterSeverita !== 'tutti') query = query.eq('severita', filterSeverita);
      if (filterReparto !== 'tutti') query = query.eq('reparto_id', filterReparto);
      if (filterDateFrom) query = query.gte('created_at', filterDateFrom);
      if (filterDateTo) query = query.lte('created_at', filterDateTo + 'T23:59:59');

      const [eventiRes, reportRes, repartiRes] = await Promise.all([
        query,
        supabase.from('report_giornalieri').select('*').order('data_report', { ascending: false }).limit(30),
        supabase.from('reparti').select('id, nome').order('nome'),
      ]);

      if (eventiRes.error) throw eventiRes.error;
      if (reportRes.error) throw reportRes.error;
      setEventi((eventiRes.data as StoricaEvento[]) || []);
      setReport((reportRes.data as ReportGiornaliero[]) || []);
      setReparti((repartiRes.data as { id: string; nome: string }[]) || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [filterTipo, filterSeverita, filterReparto, filterDateFrom, filterDateTo, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (d: string) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatDateShort = (d: string) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const generateReport = async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data: existing } = await supabase.from('report_giornalieri').select('id').eq('data_report', today).maybeSingle();
    if (existing) {
      alert('Report per oggi già presente');
      return;
    }

    const [todayAlarmsRes, devicesRes, conflittiRes, scadenzeRes, ticketsRes] = await Promise.all([
      supabase.from('allarmi').select('id, priorita, stato').gte('created_at', today + 'T00:00:00'),
      supabase.from('devices').select('id, stato, created_at'),
      supabase.from('ip_addresses').select('id', { count: 'exact', head: true }).eq('stato', 'conflitto'),
      supabase.from('scadenze').select('id', { count: 'exact', head: true }).lte('data_scadenza', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
      supabase.from('tickets').select('id, stato').gte('created_at', today + 'T00:00:00'),
    ]);

    const todayAlarms = todayAlarmsRes.data || [];
    const allDevices = devicesRes.data || [];
    const totale = todayAlarms.length;
    const critici = todayAlarms.filter(a => a.priorita === 'critico').length;
    const risolti = todayAlarms.filter(a => a.stato === 'risolto').length;
    const aperti = todayAlarms.filter(a => ['attivo','riconosciuto','in_gestione'].includes(a.stato)).length;
    const onlineCount = allDevices.filter(d => d.stato === 'online').length;
    const nuoviDisp = allDevices.filter(d => d.created_at >= today + 'T00:00:00').length;
    const ticketAperti = (ticketsRes.data || []).filter(t => !['risolto','chiuso'].includes(t.stato)).length;
    const ticketEscalation = (ticketsRes.data || []).filter(t => t.stato === 'in_attesa_utente').length;

    await supabase.from('report_giornalieri').insert({
      data_report: today,
      totale_allarmi: totale,
      allarmi_critici: critici,
      allarmi_risolti: risolti,
      allarmi_aperti: aperti,
      dispositivi_offline: allDevices.filter(d => d.stato === 'offline').length,
      dispositivi_online: onlineCount,
      nuovi_dispositivi: nuoviDisp,
      conflitti_ip: conflittiRes.count || 0,
      scadenze_prossime: scadenzeRes.count || 0,
      ticket_aperti: ticketAperti,
      ticket_escalation: ticketEscalation,
      destinatari: [],
    });
    await fetchData();
  };

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb items={[{ label: 'Storico Eventi' }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Icon name="ClockIcon" size={28} className="text-sky-400" />
            Storico Eventi
          </h1>
          <p className="text-muted-color text-sm mt-1">Log completo eventi con filtri avanzati e report giornalieri</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface rounded-xl p-1 border border-border w-fit">
        {[
          { v: 'eventi', l: 'Storico Eventi', icon: 'ClockIcon' },
          { v: 'report', l: 'Report Giornalieri', icon: 'DocumentChartBarIcon' },
        ].map(({ v, l, icon }) => (
          <button key={v} onClick={() => setActiveTab(v as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === v ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white'}`}>
            <Icon name={icon as any} size={16} />
            {l}
          </button>
        ))}
      </div>

      {activeTab === 'eventi' && (
        <>
          {/* Filters */}
          <div className="bg-surface border border-border rounded-2xl p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <select value={filterTipo} onChange={e => { setFilterTipo(e.target.value); setPage(0); }}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                <option value="tutti">Tutti i tipi</option>
                {Object.entries(EVENTO_TIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select value={filterSeverita} onChange={e => { setFilterSeverita(e.target.value); setPage(0); }}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                <option value="tutti">Tutte le severità</option>
                <option value="critico">Critico</option>
                <option value="alto">Alto</option>
                <option value="medio">Medio</option>
                <option value="basso">Basso</option>
                <option value="info">Info</option>
              </select>
              <select value={filterReparto} onChange={e => { setFilterReparto(e.target.value); setPage(0); }}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500">
                <option value="tutti">Tutti i reparti</option>
                {reparti.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
              <input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(0); }}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500" />
              <input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(0); }}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500" />
            </div>
            {(filterTipo !== 'tutti' || filterSeverita !== 'tutti' || filterReparto !== 'tutti' || filterDateFrom || filterDateTo) && (
              <button onClick={() => { setFilterTipo('tutti'); setFilterSeverita('tutti'); setFilterReparto('tutti'); setFilterDateFrom(''); setFilterDateTo(''); setPage(0); }}
                className="mt-3 text-xs text-sky-400 hover:underline flex items-center gap-1">
                <Icon name="XMarkIcon" size={12} />
                Rimuovi filtri
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">{error}</div>
          ) : (
            <div className="space-y-2">
              {eventi.length === 0 ? (
                <div className="text-center py-12 text-muted-color">
                  <Icon name="ClockIcon" size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Nessun evento trovato</p>
                </div>
              ) : eventi.map(e => {
                const sev = SEVERITA_CONFIG[e.severita] || SEVERITA_CONFIG.info;
                const icon = EVENTO_TIPO_ICONS[e.tipo] || 'InformationCircleIcon';
                return (
                  <div key={e.id} className="bg-surface border border-border rounded-xl p-3 hover:border-gray-600 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.bg}`}>
                        <Icon name={icon as any} size={16} className={sev.text} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sev.dot}`} />
                          <span className="font-semibold text-white text-sm">{e.titolo}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-surface-elevated text-muted-color">
                            {EVENTO_TIPO_LABELS[e.tipo] || e.tipo}
                          </span>
                        </div>
                        {e.descrizione && <p className="text-xs text-muted-color mt-0.5 line-clamp-1">{e.descrizione}</p>}
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-color flex-wrap">
                          {e.devices && <span className="flex items-center gap-1"><Icon name="ServerIcon" size={10} />{e.devices.hostname}</span>}
                          {e.reparti && <span className="flex items-center gap-1"><Icon name="BuildingOffice2Icon" size={10} />{e.reparti.nome}</span>}
                          {e.user_profiles && <span className="flex items-center gap-1"><Icon name="UserIcon" size={10} />{e.user_profiles.full_name}</span>}
                          <span className="ml-auto flex items-center gap-1"><Icon name="ClockIcon" size={10} />{formatDate(e.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="flex items-center gap-1 px-3 py-1.5 bg-surface-elevated text-muted-color rounded-lg text-sm disabled:opacity-40 hover:text-white transition-colors">
                  <Icon name="ChevronLeftIcon" size={14} />
                  Precedente
                </button>
                <span className="text-sm text-muted-color">Pagina {page + 1}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={eventi.length < PAGE_SIZE}
                  className="flex items-center gap-1 px-3 py-1.5 bg-surface-elevated text-muted-color rounded-lg text-sm disabled:opacity-40 hover:text-white transition-colors">
                  Successiva
                  <Icon name="ChevronRightIcon" size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'report' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={generateReport}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-xl font-semibold text-sm hover:bg-sky-600 transition-colors">
              <Icon name="DocumentPlusIcon" size={16} />
              Genera Report Oggi
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : report.length === 0 ? (
            <div className="text-center py-12 text-muted-color">
              <Icon name="DocumentChartBarIcon" size={40} className="mx-auto mb-3 opacity-50" />
              <p>Nessun report disponibile</p>
            </div>
          ) : report.map(r => (
            <div key={r.id} className="bg-surface border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-white text-lg">{formatDateShort(r.data_report)}</h3>
                  <p className="text-xs text-muted-color">Report giornaliero rete</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.inviato_email ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={12} />
                      Email Inviata
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400">Non Inviato</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Totale Allarmi', value: r.totale_allarmi, color: 'text-white' },
                  { label: 'Critici', value: r.allarmi_critici, color: 'text-red-400' },
                  { label: 'Risolti', value: r.allarmi_risolti, color: 'text-emerald-400' },
                  { label: 'Aperti', value: r.allarmi_aperti, color: 'text-orange-400' },
                  { label: 'Dispositivi Offline', value: r.dispositivi_offline, color: 'text-red-400' },
                  { label: 'Nuovi Dispositivi', value: r.nuovi_dispositivi, color: 'text-sky-400' },
                  { label: 'Conflitti IP', value: r.conflitti_ip, color: 'text-yellow-400' },
                  { label: 'Scadenze Prossime', value: r.scadenze_prossime, color: 'text-orange-400' },
                  { label: 'Ticket Aperti', value: r.ticket_aperti, color: 'text-blue-400' },
                  { label: 'Escalation', value: r.ticket_escalation, color: 'text-red-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-surface-elevated rounded-xl p-3 text-center">
                    <div className={`text-2xl font-bold ${color}`}>{value}</div>
                    <div className="text-xs text-muted-color mt-1">{label}</div>
                  </div>
                ))}
              </div>
              {r.destinatari?.length > 0 && (
                <div className="mt-3 text-xs text-muted-color flex items-center gap-1">
                  <Icon name="EnvelopeIcon" size={12} />
                  Destinatari: {r.destinatari.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
